import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import indoorClimb from "@/assets/hero/indoor-climb.jpg";
import yellowHold from "@/assets/hero/yellow-hold.jpg";
import greenYellowWall from "@/assets/hero/green-yellow-wall.jpg";
import overheadBoulder from "@/assets/hero/overhead-boulder.jpg";

const heroImages = [
  indoorClimb,
  yellowHold,
  greenYellowWall,
  overheadBoulder,
];

const Hero = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 8000); // Change image every 8 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image Carousel */}
      <div className="absolute inset-0 z-0">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-2000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img 
              src={image} 
              alt={
                index === 0 ? "Professional indoor climbing gym construction with modern bouldering walls and training areas" :
                index === 1 ? "Commercial climbing wall installation featuring colorful holds and professional route setting" :
                index === 2 ? "Custom bouldering gym design with green and yellow climbing walls - turnkey facility construction" :
                "Overhead view of indoor climbing facility showing professional boulder wall installation and safety surfaces"
              }
              className="w-full h-full object-cover scale-105"
              width={1920}
              height={1280}
              fetchPriority={index === 0 ? "high" : "low"}
              decoding={index === 0 ? "sync" : "async"}
              loading={index === 0 ? "eager" : "lazy"}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/85 via-steel/75 to-charcoal/60" />
        <div className="absolute inset-0 dot-pattern opacity-5" />
      </div>
      
      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20 sm:py-24 pt-36 sm:pt-44">
        <div className="max-w-4xl">
          <div className="animate-fade-in-up">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-display text-white mb-8 leading-tight tracking-tight">
              COMPLETE CLIMBING GYM
              <span className="block text-gradient-primary mt-2">DESIGN & CONSTRUCTION</span>
            </h1>
          </div>
          
          <div className="animate-fade-in-up delay-200">
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-12 max-w-3xl leading-relaxed font-light">
              Professional climbing gym construction company providing turnkey bouldering gym builds from initial concept to final handover. Our in-house team handles commercial climbing wall design, CNC manufacturing, professional installation, and facility commissioning—ensuring seamless coordination and world-class climbing facility results across Europe.
            </p>
          </div>
          
          <div className="animate-fade-in-up delay-300">
            <Button 
              size="lg" 
              asChild
              className="bg-gradient-primary text-white text-lg px-12 py-7 shadow-2xl hover:shadow-[0_20px_60px_rgba(255,103,31,0.4)] hover:scale-105 transition-all group border-0"
            >
              <a href="#contact">
                Contact Us
                <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" size={24} />
              </a>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Image Indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 flex gap-3">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            className={`h-1.5 rounded-full transition-all ${
              index === currentImageIndex 
                ? 'bg-primary w-12' 
                : 'bg-white/30 w-8 hover:bg-white/50'
            }`}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>
      
      {/* Decorative Elements */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-primary/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-1/2 h-1 bg-gradient-primary" />
    </section>
  );
};

export default Hero;
